import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { InitialView } from './components/InitialView';
import { ChatView } from './components/ChatView';
import { EvidenceTable } from './pages/EvidenceTable';
import { TranscriptEditor } from './pages/TranscriptEditor';
import { FieldNotesFeed } from './pages/FieldNotesFeed';
import { IncidentSurface } from './pages/IncidentSurface';
import { PassOns } from './pages/PassOns';
import { ReportEditor } from './pages/ReportEditor';
import { AffidavitModal } from './components/AffidavitModal';
export type Message = {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
};
export function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAffidavitOpen, setIsAffidavitOpen] = useState(false);
  const exampleConversation: Message[] = [{
    id: '1',
    content: "Can you help me understand our agency's policy on body camera usage during traffic stops?",
    role: 'user',
    timestamp: new Date(Date.now() - 1000000)
  }, {
    id: '2',
    content: "I'll help you understand the body camera policy for traffic stops. According to your agency's guidelines:\n\n1. Officers must activate cameras before any citizen contact\n2. Recording must continue uninterrupted throughout the entire interaction\n3. Officers should announce they are recording\n4. Cameras can only be deactivated after the interaction is completely finished\n\nWould you like me to elaborate on any of these points?",
    role: 'assistant',
    timestamp: new Date(Date.now() - 900000)
  }, {
    id: '3',
    content: "Yes, could you clarify when exactly I should start recording? Sometimes I'm not sure about the timing.",
    role: 'user',
    timestamp: new Date(Date.now() - 800000)
  }, {
    id: '4',
    content: "The policy specifies that you should activate your body camera:\n\n• As soon as you decide to initiate a traffic stop\n• Before activating emergency lights and sirens\n• At least 30 seconds before any planned citizen contact\n\nThis ensures you capture the entire context of the stop, including your initial observations. The early activation is important for transparency and complete documentation. Remember, it's better to start recording too early than too late.",
    role: 'assistant',
    timestamp: new Date(Date.now() - 700000)
  }];
  const handleSendMessage = async (content: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      role: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    let aiResponse = "I understand you're asking about that. Let me help you with this.";
    if (content.toLowerCase().includes('hello') || content.toLowerCase().includes('hi')) {
      aiResponse = 'Hello! How can I assist you today?';
    } else if (content.toLowerCase().includes('help')) {
      aiResponse = "I'm here to help! What specific assistance do you need?";
    } else if (content.toLowerCase().includes('thank')) {
      aiResponse = "You're welcome! Is there anything else you'd like to know?";
    } else if (content.length < 10) {
      aiResponse = "Could you please provide more details about what you'd like to know?";
    } else {
      aiResponse = `Thank you for your detailed query. Let me break this down:
1. First, let's consider the main points you've raised.
2. Based on my analysis, I can help you with this specific situation.
3. Would you like me to provide more detailed information about any particular aspect?`;
    }
    const assistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      content: aiResponse,
      role: 'assistant',
      timestamp: new Date()
    };
    setIsLoading(false);
    setMessages(prev => [...prev, assistantMessage]);
  };
  return <BrowserRouter data-id="element-0">
      <div className="flex w-full h-screen bg-command-app text-[#e0e0e0]" data-id="element-1">
        <Routes data-id="element-2">
          <Route path="/transcript/:id" element={<>
                <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-4" />
                <TranscriptEditor onOpenAffidavit={() => setIsAffidavitOpen(true)} data-id="element-5" />
              </>} data-id="element-3" />
          <Route path="/chat/:id" element={<>
                <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-7" />
                <ChatView messages={messages} isLoading={isLoading} onSendMessage={handleSendMessage} isVoiceMode={isVoiceMode} onToggleVoiceMode={() => setIsVoiceMode(!isVoiceMode)} data-id="element-8" />
              </>} data-id="element-6" />
          <Route path="/notes/feed" element={<>
                <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-10" />
                <FieldNotesFeed data-id="element-11" />
              </>} data-id="element-9" />
          <Route path="/notes/incident/:id" element={<>
                <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-13" />
                <IncidentSurface data-id="element-14" />
              </>} data-id="element-12" />
          <Route path="/report/:id" element={<ReportEditor data-id="element-16" />} data-id="element-15" />
          <Route path="/passons" element={<>
                <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-18" />
                <PassOns data-id="element-19" />
              </>} data-id="element-17" />
          <Route path="/evidence" element={<>
                <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-21" />
                <EvidenceTable data-id="element-22" />
              </>} data-id="element-20" />
          <Route path="/" element={<>
                <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-24" />
                <main className="flex-grow flex flex-col h-screen overflow-hidden" data-id="element-25">
                  {messages.length === 0 ? <InitialView onSendMessage={handleSendMessage} isVoiceMode={isVoiceMode} onToggleVoiceMode={() => setIsVoiceMode(!isVoiceMode)} data-id="element-26" /> : <ChatView messages={messages} isLoading={isLoading} onSendMessage={handleSendMessage} isVoiceMode={isVoiceMode} onToggleVoiceMode={() => setIsVoiceMode(!isVoiceMode)} data-id="element-27" />}
                </main>
              </>} data-id="element-23" />
        </Routes>
        <AffidavitModal isOpen={isAffidavitOpen} onClose={() => setIsAffidavitOpen(false)} onConfirm={() => setIsAffidavitOpen(false)} data-id="element-28" />
      </div>
    </BrowserRouter>;
}